import { arrivalKeepFor, lastAnswersFor } from '@agavi/blocks';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';

import type { TestBarChip } from './collapsed-input-block';
import type { ArrivalPolicy, AttemptRecord, FeedbackState } from '@agavi/blocks';

import { useRunSession } from '@/components/preview-session-context';

/**
 * WHAT each block decided, as opposed to how it looked.
 *
 * `readBlockResults` already reads a verdict per block out of the DOM, and that is the right tool
 * for the job it does: the summary dialog needs three numbers and a name, and reading `data-eval`
 * gets them for all eleven block types without any of them growing a prop.
 *
 * `resolveFlow` cannot run on that. Its whole ladder is made of TRIGGERS — `isIncompleteTrigger`,
 * the two sensor rungs, and rules matching a specific `stateId` — and a `ChipEvaluation` has
 * thrown every one of them away. Routing a student on "something was incorrect" when the author
 * wrote "incorrect BECAUSE the compass never read" is the distinction the sensor work exists for.
 *
 * So this carries the states themselves. It is deliberately NOT a second copy of the verdict: the
 * dialog keeps reading the DOM, because what a student SEES is the honest source for what to tell
 * them it was, and Flow reads this, because what the blocks DECIDED is the honest source for where
 * to send them.
 *
 * ## Why a registry and not a prop
 *
 * The eleven `renderXFiredFeedback` helpers are module-level functions; `blockId` is not in scope
 * in any of them, so a prop would mean changing both the helper and its call site in all eleven
 * blocks. The id is already on the wrapper every block renders inside (`id="block-..."`, which is
 * how `readBlockResults` finds them), so handing it down as context costs one line there and none
 * in any block.
 *
 * Reporting happens in an EFFECT, never during render. A render-phase write to the ref would
 * survive a render React discarded and restarted, leaving the registry describing states no
 * student was ever shown — the same hazard `use-restored-data` documents for its own marker.
 */

/**
 * Which block, on which task, the subtree being rendered belongs to. Null outside a block.
 *
 * The TASK is in here because a block has no idea which one it is on -- `taskId` reaches the
 * eleven only as an unused member of `sharedProps` -- and asking "what did I have here before"
 * needs both halves. Putting it in the context the block already sits in keeps that question to
 * one argument-free hook rather than a prop threaded through eleven signatures.
 */
interface BlockIdentity { blockId: string; taskId?: string }

const BlockIdContext = createContext<BlockIdentity | null>(null);

/**
 * Which block the subtree being rendered belongs to, or null outside one.
 *
 * Exported so a SHARED component can answer a question about "this block" without the block
 * passing itself down — `IssuesPanel` uses it to report a dead answer reference, which is the
 * one issue no block can compute for itself.
 */
export function useBlockIdentity(): BlockIdentity | null {
  return useContext(BlockIdContext);
}

export function BlockIdProvider({ blockId, taskId, children }: BlockIdentity & { children: React.ReactNode }) {
  const value = useMemo(() => ({ blockId, taskId }), [blockId, taskId]);
  return <BlockIdContext.Provider value={value}>{children}</BlockIdContext.Provider>;
}

export interface FiredStatesRegistry {
  report: (blockId: string, states: FeedbackState[]) => void;
  forget: (blockId: string) => void;
  /** A snapshot in `resolveFlow`'s own shape. Blocks that fired nothing are absent, not empty. */
  read: () => Record<string, FeedbackState[]>;
  /**
   * What the student actually submitted, as opposed to what the block made of it.
   *
   * A second map rather than a second provider: the same blocks report both, at the same moment,
   * and one grader reads both. Splitting them would be two contexts to keep in step for no gain.
   *
   * Deliberately `unknown` -- eleven blocks have eleven answer shapes, and the registry has no
   * business knowing them. Only the block that wrote one ever reads it back.
   */
  reportAnswer: (blockId: string, answer: unknown) => void;
  readAnswers: () => Record<string, unknown>;
  /**
   * THE BLOCK'S OWN TEST LEGEND, so a reader does not have to work out what it is asking.
   *
   * Every chip surface is downstream of one derivation, and the one thing that cannot be reached
   * from outside a block is WHICH of its conditions are in force right now -- that depends on the
   * version on show, the roll, and any scenario or branch the block resolved for itself. The
   * Behind the Scenes panel read the base feedback instead and told a teacher the answer was
   * Positive on a block whose scenario had swapped it.
   *
   * So the block says. A `TestBarChip` already carries `active`, so this is the legend and the
   * highlighting in one channel rather than two kept in step.
   */
  reportTest: (blockId: string, chips: TestBarChip[], states: FeedbackState[]) => void;
  /**
   * The chips as they stand. A STABLE object between reports, because `useSyncExternalStore`
   * compares what this returns and a fresh one each call is an infinite render.
   */
  readChips: () => Record<string, TestBarChip[]>;
  /**
   * Wake a reader when they change.
   *
   * The rest of this registry is refs on purpose: a block reporting its verdict must not
   * re-render the task around it. A READER is the opposite case -- the Behind the Scenes panel
   * exists to show what the blocks are saying, and a ref written during their render would never
   * wake it. So reports notify, and only the subscriber re-renders.
   */
  subscribeChips: (listener: () => void) => () => void;
  /**
   * WHAT WOULD FIRE IF THEY SUBMITTED NOW, per block.
   *
   * The chips say which conditions light; this says the same thing in the shape the flow ladder
   * eats, so the panel can ask `resolveFlowOutcome` where the student would be sent. Reported by
   * the blocks that derive a draft at all -- the rest never differ from their submitted state,
   * so their absence here is correct rather than missing. Written by `reportTest` above: the
   * two halves are one fact about one block at one moment, and the reader must never see a
   * frame holding one of them.
   */
  readDraftStates: () => Record<string, FeedbackState[]>;
}

const FiredStatesContext = createContext<FiredStatesRegistry | null>(null);

/**
 * The registry is CREATED by whoever grades, not by the provider.
 *
 * Grading reads the snapshot from a component that is necessarily above the blocks it is grading,
 * which is exactly where a context value is not visible. So the page holds the registry and hands
 * it down, rather than asking for it from a position where the answer would always be null.
 */
export function useCreateFiredStates(): FiredStatesRegistry {
  const map = useRef(new Map<string, FeedbackState[]>());
  const answers = useRef(new Map<string, unknown>());
  const chips = useRef(new Map<string, TestBarChip[]>());
  const chipsSnapshot = useRef<Record<string, TestBarChip[]>>({});
  const chipListeners = useRef(new Set<() => void>());
  const drafts = useRef(new Map<string, FeedbackState[]>());
  const draftsSnapshot = useRef<Record<string, FeedbackState[]>>({});

  const report = useCallback((blockId: string, states: FeedbackState[]) => {
    if (states.length === 0) map.current.delete(blockId);
    else map.current.set(blockId, states);
  }, []);
  const forget = useCallback((blockId: string) => {
    map.current.delete(blockId);
    answers.current.delete(blockId);
    if (chips.current.delete(blockId)) {
      chipsSnapshot.current = Object.fromEntries(chips.current);
      for (const listener of chipListeners.current) listener();
    }
  }, []);
  const read = useCallback(() => Object.fromEntries(map.current), []);

  const reportAnswer = useCallback((blockId: string, answer: unknown) => {
    // `undefined` is "nothing submitted yet", which is not an answer and must not be recorded as
    // one -- an attempt whose answers map holds explicit undefineds reads, on export, as a
    // student who answered nothing rather than one whose block had not been reached.
    if (answer === undefined) answers.current.delete(blockId);
    else answers.current.set(blockId, answer);
  }, []);
  const readAnswers = useCallback(() => Object.fromEntries(answers.current), []);

  /**
   * Write one map, say whether it moved. Rebuilt here rather than in the reader, so the reader
   * can hand back the same object twice and `useSyncExternalStore` can tell "nothing changed"
   * from "changed to an equal value".
   */
  const put = <T,>(
    into: React.MutableRefObject<Map<string, T[]>>,
    snapshot: React.MutableRefObject<Record<string, T[]>>,
    blockId: string,
    next: T[],
  ): boolean => {
    const prev = into.current.get(blockId);
    if (next.length === 0) {
      if (prev === undefined) return false;
      into.current.delete(blockId);
    } else {
      if (prev === next) return false;
      into.current.set(blockId, next);
    }
    snapshot.current = Object.fromEntries(into.current);
    return true;
  };

  /*
   * BOTH HALVES, ONE NOTIFICATION. They are one fact about one block at one moment, reported by
   * the same component in the same effect, and a reader woken between them sees a block whose
   * chips and whose draft states disagree. Two writers used to notify separately, which was two
   * renders of the debug panel for every keystroke in a block.
   */
  const reportTest = useCallback((blockId: string, nextChips: TestBarChip[], nextStates: FeedbackState[]) => {
    const moved = put(chips, chipsSnapshot, blockId, nextChips);
    const alsoMoved = put(drafts, draftsSnapshot, blockId, nextStates);
    if (!moved && !alsoMoved) return;
    for (const listener of chipListeners.current) listener();
  }, []);
  const readChips = useCallback(() => chipsSnapshot.current, []);
  const readDraftStates = useCallback(() => draftsSnapshot.current, []);
  const subscribeChips = useCallback((listener: () => void) => {
    chipListeners.current.add(listener);
    return () => { chipListeners.current.delete(listener); };
  }, []);

  return useMemo(
    () => ({ report, forget, read, reportAnswer, readAnswers, reportTest, readChips, subscribeChips,
      readDraftStates }),
    [report, forget, read, reportAnswer, readAnswers, reportTest, readChips, subscribeChips,
      readDraftStates],
  );
}

/**
 * Wrapped around a surface that needs to route — Preview. The editor's canvas cards and the
 * per-block test widget render `FiredFeedback` too, and both should report nowhere, so the hook
 * no-ops without a provider rather than every call site asking whether it is in one.
 */
export function FiredStatesProvider({ value, children }: {
  value: FiredStatesRegistry | null;
  children: React.ReactNode;
}) {
  return <FiredStatesContext.Provider value={value}>{children}</FiredStatesContext.Provider>;
}

/**
 * The past visit being reviewed, or null while the student is answering live.
 *
 * Review and revisit answer two different questions and must not share a mechanism. A REVISIT is
 * the student arriving back at a task to carry on, and shows the latest thing they handed in. A
 * REVIEW is a teacher stepping back through earlier visits, locked, and shows one PARTICULAR
 * attempt -- including ones later superseded, which is the whole point of looking.
 */
const ReviewAttemptContext = createContext<AttemptRecord | null>(null);

export function ReviewAttemptProvider({ attempt, children }: {
  attempt: AttemptRecord | null;
  children: React.ReactNode;
}) {
  return <ReviewAttemptContext.Provider value={attempt}>{children}</ReviewAttemptContext.Provider>;
}

/**
 * WHAT THIS TASK KEEPS ON A RETURN, or nothing where the question does not arise.
 *
 * A context rather than a prop because the reader is `usePriorAnswer`, which every block calls
 * from inside its own state initializer and which knows only which block it is. Threading a
 * policy through eleven blocks to reach one hook is the shape a context exists to avoid.
 *
 * `null` is "no policy", which is a task authored before the field existed, a task nobody can
 * return to, and the editor's own canvas alike. All three want the default, and the default is
 * what `arrivalKeepFor` gives for an absent policy.
 */
const ArrivalPolicyContext = createContext<ArrivalPolicy | null>(null);

export function ArrivalPolicyProvider({ policy, children }: {
  policy: ArrivalPolicy | null;
  children: React.ReactNode;
}) {
  return <ArrivalPolicyContext.Provider value={policy}>{children}</ArrivalPolicyContext.Provider>;
}

/** Called by `FiredFeedback`, once, so all eleven blocks report by rendering their feedback. */
export function useReportFiredStates(states: FeedbackState[] | null): void {
  const blockId = useContext(BlockIdContext)?.blockId;
  const registry = useContext(FiredStatesContext);

  useEffect(() => {
    if (!blockId || !registry) return;
    registry.report(blockId, states ?? []);
  }, [blockId, registry, states]);

  /*
   * THE SAME SPLIT `useReportTestChips` MAKES, and this is where it was actually costing
   * something. "Clearing on every re-run is harmless: the report above re-adds it immediately"
   * was true while nothing watched -- and then `forget` grew a notification, because it also
   * drops the block's CHIPS and the Preview panel subscribes to those.
   *
   * So on submit, when a block's fired states changed, this cleanup wiped its chips, woke the
   * panel, and the row fell back to the authored condition for about four frames before the
   * chips were reported again. Measured in the browser as exactly that: a 12-chip row dropping
   * to the 1-chip fallback and back, with identical text either side of it. On screen, a flash
   * per block per submit.
   *
   * `forget` also drops the block's recorded ANSWER, so those four frames were an attempt with
   * no answer in it for anything that read one.
   */
  useEffect(() => {
    if (!blockId || !registry) return undefined;
    // Unmounting is a block leaving the task, which must not leave its last verdict behind to
    // be routed on.
    return () => registry.forget(blockId);
  }, [blockId, registry]);
}

/**
 * Report what this block submitted, so the attempt records it.
 *
 * Called by the block rather than by `FiredFeedback`, because the answer is the block's own and
 * there is no shared component holding it -- the eleven keep it in eleven differently-named and
 * differently-shaped pieces of state. What they DO share is the moment: each sets a `submitted...`
 * value when Preview's signal fires, and that value is exactly what belongs here.
 *
 * Pass the SUBMITTED value, never the in-progress one. The draft changes on every keystroke and
 * recording it would make the attempt a snapshot of the student's typing rather than of what they
 * chose to hand in.
 */
export function useReportAnswer(answer: unknown): void {
  const blockId = useContext(BlockIdContext)?.blockId;
  const registry = useContext(FiredStatesContext);

  useEffect(() => {
    if (!blockId || !registry) return;
    registry.reportAnswer(blockId, answer);
  }, [blockId, registry, answer]);

  /* Same split, same reason -- see `useReportFiredStates`. This channel has no subscriber, so
     its version of the bug is a frame in which the block has no recorded answer rather than a
     frame on screen; it is wrong in the same way and one edit away from being visible. */
  useEffect(() => {
    if (!blockId || !registry) return undefined;
    return () => registry.reportAnswer(blockId, undefined);
  }, [blockId, registry]);
}

/**
 * What this block was last submitted with, on a task the student has already been through.
 *
 * The other half of "walking back into a task does not wipe it". Counts and rolls already survive
 * a revisit because they live on the session; the typed answer did not, so a student sent to a
 * remediation task and back found the work they had done gone.
 *
 * Read at MOUNT, through a `useState` initializer, which is why it returns a value rather than
 * driving an effect. A block that re-seeded from this on every render would fight the student's
 * own typing; one that reads it once gets the revisit right and then gets out of the way. Preview
 * remounts blocks on navigation and on Reset, so once is enough.
 *
 * `undefined` means "nothing submitted here yet", which is distinct from an empty answer -- the
 * registry drops undefined rather than recording it for exactly that reason.
 */
export function usePriorAnswer<T>(): T | undefined {
  const identity = useContext(BlockIdContext);
  const reviewing = useContext(ReviewAttemptContext);
  const policy = useContext(ArrivalPolicyContext);
  const session = useRunSession();
  if (!identity) return undefined;
  // A review names the attempt; a revisit asks for the latest. Blocks do not need to know which
  // of the two is happening, only what to show.
  //
  // REVIEW IS EXEMPT FROM THE ARRIVAL POLICY. A teacher stepping back through earlier visits is
  // asking what was handed in, and "this task starts fresh" is an answer to a different
  // question -- it decides what the STUDENT finds, not what the log is allowed to show.
  if (reviewing) return reviewing.answers[identity.blockId] as T | undefined;
  if (!identity.taskId || !session) return undefined;
  /*
   * THE WINDOW IS THE POLICY. Keeping answers means the whole session's last submission comes
   * back; clearing them means only THIS visit's does -- which is not the same as nothing, and
   * that distinction is the one a naive implementation loses. A block remounting mid-visit
   * (leaving review, a re-key) must find the student's own work still there; "fresh on arrival"
   * is about arriving, not about every mount.
   */
  const within = arrivalKeepFor(policy ?? undefined, identity.blockId).answers ? 'session' : 'visit';
  return lastAnswersFor(session, identity.taskId, within)[identity.blockId] as T | undefined;
}

/**
 * Report this block's legend, live.
 *
 * Called beside the block's own chip derivation, where the resolved version, roll, scenario and
 * branch are all already in hand -- which is the whole reason the report exists rather than the
 * reader deriving it. Clears on unmount for the same reason `useReportFiredStates` does: a block
 * that has left the task must not leave its conditions behind to be read as current -- but ONLY
 * on unmount; see the second effect.
 */
function useReportTestChips(chips: TestBarChip[] | null, states: FeedbackState[] | null): void {
  const blockId = useContext(BlockIdContext)?.blockId;
  const registry = useContext(FiredStatesContext);

  useEffect(() => {
    if (!blockId || !registry) return;
    registry.reportTest(blockId, chips ?? [], states ?? []);
  }, [blockId, registry, chips, states]);

  /*
   * THE CLEAR IS ITS OWN EFFECT, and this is not tidiness.
   *
   * Both halves used to live in the effect above, so every CONTENT change ran the cleanup on
   * the way past: React runs a changed effect's destroy before its create, which emptied this
   * block out of the registry and notified, then put it back and notified again. Readers go
   * through `useSyncExternalStore`, whose listener forces a re-render where it is called -- so
   * each submit repainted the debug panel once with the block holding nothing and once with
   * its real chips. On screen that is a flash, per block, per submit.
   *
   * Depending on the IDENTITY only, the clear now runs when the block unmounts or becomes a
   * different block, which is the case it was written for: a block that has left the task must
   * not leave its conditions behind to be read as current. Cleanups run before setups across
   * the whole commit, so a changed `blockId` still clears the old entry before the new one is
   * reported.
   */
  useEffect(() => {
    if (!blockId || !registry) return undefined;
    return () => { registry.reportTest(blockId, [], []); };
  }, [blockId, registry]);
}

/**
 * A COMPONENT, not a hook, for the same reason `FiredFeedback` is one.
 *
 * A block's chips are derived inside whichever branch is rendering -- the editor's card, the
 * phone's test tab, Preview's student surface -- and a hook called from a branch is a hook called
 * conditionally. Rendering a component there is legal anywhere, and it puts the report beside the
 * feedback report that already works this way.
 *
 * Renders nothing.
 */
/**
 * The registry, for a reader rather than a reporter.
 *
 * The Behind the Scenes panel sits above the blocks, inside the provider, and reads what they
 * have said. Null outside a provider, which is every surface that is not a task.
 */
export function useFiredStatesRegistry(): FiredStatesRegistry | null {
  return useContext(FiredStatesContext);
}

export function ReportTestChips({ chips, states = null }: {
  chips: TestBarChip[] | null;
  /** The draft's fired states, from the blocks that derive one. Absent is "same as submitted". */
  states?: FeedbackState[] | null;
}): null {
  useReportTestChips(chips, states);
  return null;
}
